import { PDFDocument, PDFFont, PDFPage, rgb, StandardFonts } from "pdf-lib";
import { Country, Shipment } from "../types";

/**
 * House Bill of Lading (HBL) PDF — sea shipments only. Black & white box layout
 * mirroring the printed HBL document:
 *   Shipper            | B/L No. + B/L date
 *   Consignee          | No. of original B/Ls + B/L type
 *   Notify party       |
 *   Place of receipt | Port of loading | Vessel / Voyage
 *   Port of discharge | Place of delivery
 *   Cargo details (full width)
 *   Freight & charges | Remarks
 *   Place of issue | Date of issue | Signed by (carrier / agent)
 * Place of delivery falls back to the delivery address when the field is empty.
 */

function safe<T>(fn: () => T, fallback = ""): string {
  try {
    const val = fn();
    return val !== undefined && val !== null && val !== ""
      ? String(val)
      : fallback;
  } catch {
    return fallback;
  }
}

function wrapText(
  text: string,
  font: PDFFont,
  size: number,
  maxWidth: number,
  maxLines = 6,
): string[] {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    } else {
      line = candidate;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  return lines;
}

const BL_TYPE_LABELS: Record<string, string> = {
  ORIGINAL: "Original",
  SEAWAY: "Seaway Bill",
  TELEX: "Telex Release",
};

const ORIGINALS_LABELS: Record<string, string> = {
  "1": "ONE (1)",
  "2": "TWO (2)",
  "3": "THREE (3)",
};

export async function createBillOfLadingPdf(
  data: Shipment,
  countries: Country[],
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.setTitle(
    `House Bill of Lading${
      data.referenceNumber ? ` - ${data.referenceNumber}` : ""
    }`,
  );
  pdfDoc.setAuthor("LiveCargo");
  pdfDoc.setSubject("House Bill of Lading");
  pdfDoc.setCreationDate(new Date());

  const page: PDFPage = pdfDoc.addPage([595, 842]); // A4
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Black & white palette only.
  const black = rgb(0, 0, 0);
  const labelGray = rgb(0.4, 0.4, 0.4);
  const border = rgb(0, 0, 0);
  const bannerBg = rgb(0.9, 0.9, 0.9);

  const margin = 36;
  const innerWidth = width - 2 * margin;
  let yPos = height - margin;

  const countryOptions = (countries ?? []).flat().map((c) => ({
    value: c._id,
    label: c.name,
  }));
  const countryName = (codeOrName: any): string => {
    if (!codeOrName) return "";
    const code = typeof codeOrName === "string" ? codeOrName : codeOrName?._id;
    if (!code) return "";
    const found = countryOptions.find(
      (c) => c.value?.toLowerCase?.() === code.toLowerCase?.(),
    );
    return found?.label || (typeof codeOrName === "string" ? "" : "");
  };

  const sea: any = data.seaDetails ?? {};

  const fmtDate = (d?: string) => {
    if (!d) return "";
    const date = new Date(d);
    if (isNaN(date.getTime())) return d.split("T")[0];
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${date.getFullYear()}`;
  };

  const partyLines = (party: any): string[] => {
    if (!party) return [];
    const addr = party.address ?? {};
    return [
      safe(() => party.companyName),
      safe(() => addr.street?.split(",")[0]),
      [addr.postalCode, addr.city].filter(Boolean).join(" "),
      countryName(addr.country),
    ].filter((l) => l && l !== "");
  };

  // Draw a labelled cell with wrapped value lines.
  const drawCell = (
    x: number,
    top: number,
    w: number,
    h: number,
    label: string,
    valueLines: string[],
    opts: { valueSize?: number; valueBold?: boolean } = {},
  ) => {
    page.drawRectangle({
      x,
      y: top - h,
      width: w,
      height: h,
      borderColor: border,
      borderWidth: 1,
    });
    page.drawText(label, {
      x: x + 6,
      y: top - 11,
      size: 7,
      font,
      color: labelGray,
    });
    const size = opts.valueSize ?? 9;
    let vy = top - 23;
    for (const line of valueLines) {
      if (!line) continue;
      if (vy < top - h + 5) break;
      page.drawText(line, {
        x: x + 6,
        y: vy,
        size,
        font: opts.valueBold ? boldFont : font,
        color: black,
      });
      vy -= size + 3;
    }
  };

  // Cell with two stacked label/value pairs (B/L No.+date, originals+type).
  const drawStackedCell = (
    x: number,
    top: number,
    w: number,
    h: number,
    pairs: { label: string; value: string; bold?: boolean }[],
  ) => {
    page.drawRectangle({
      x,
      y: top - h,
      width: w,
      height: h,
      borderColor: border,
      borderWidth: 1,
    });
    let cy = top - 11;
    for (const p of pairs) {
      page.drawText(p.label, {
        x: x + 6,
        y: cy,
        size: 7,
        font,
        color: labelGray,
      });
      cy -= 12;
      page.drawText(p.value, {
        x: x + 6,
        y: cy,
        size: 9,
        font: p.bold ? boldFont : font,
        color: black,
      });
      cy -= 12;
    }
  };

  // ── Title banner ────────────────────────────────────────────────────────────
  const bannerH = 22;
  page.drawRectangle({
    x: margin,
    y: yPos - bannerH,
    width: innerWidth,
    height: bannerH,
    color: bannerBg,
    borderColor: border,
    borderWidth: 1,
  });
  const title = "HOUSE BILL OF LADING";
  const titleW = boldFont.widthOfTextAtSize(title, 12);
  page.drawText(title, {
    x: (width - titleW) / 2,
    y: yPos - 15,
    size: 12,
    font: boldFont,
    color: black,
  });
  yPos -= bannerH;

  const leftW = innerWidth * 0.6;
  const rightW = innerWidth - leftW;

  // ── Shipper | B/L No. + date ────────────────────────────────────────────────
  const rowH1 = 70;
  drawCell(margin, yPos, leftW, rowH1, "Shipper / Exporter", [
    ...partyLines(data.pickup),
  ]);
  drawStackedCell(margin + leftW, yPos, rightW, rowH1, [
    { label: "B/L No.", value: safe(() => sea.blNumber), bold: true },
    { label: "B/L date", value: fmtDate(sea.blDate) },
  ]);
  yPos -= rowH1;

  // ── Consignee | No. of originals + B/L type ─────────────────────────────────
  const rowH2 = 70;
  drawCell(margin, yPos, leftW, rowH2, "Consignee", [
    ...partyLines(data.delivery),
  ]);
  drawStackedCell(margin + leftW, yPos, rightW, rowH2, [
    {
      label: "No. of original B/Ls",
      value: sea.numberOfOriginals
        ? ORIGINALS_LABELS[String(sea.numberOfOriginals)] ||
          String(sea.numberOfOriginals)
        : "",
      bold: true,
    },
    {
      label: "B/L type",
      value: sea.blType ? BL_TYPE_LABELS[sea.blType] ?? sea.blType : "",
    },
  ]);
  yPos -= rowH2;

  // ── Notify party | (empty) ──────────────────────────────────────────────────
  const rowH3 = 50;
  const notify = sea.notifyParty ?? {};
  drawCell(margin, yPos, leftW, rowH3, "Notify party", [
    safe(() => notify.company),
    safe(() => notify.contact),
    [safe(() => notify.email), safe(() => notify.phone)]
      .filter(Boolean)
      .join("  "),
  ]);
  page.drawRectangle({
    x: margin + leftW,
    y: yPos - rowH3,
    width: rightW,
    height: rowH3,
    borderColor: border,
    borderWidth: 1,
  });
  yPos -= rowH3;

  // ── Place of receipt | Port of loading | Vessel / Voyage ────────────────────
  const rowH4 = 44;
  const third = innerWidth / 3;
  drawCell(margin, yPos, third, rowH4, "Place of receipt", [
    safe(() => sea.placeOfReceipt),
  ]);
  drawCell(margin + third, yPos, third, rowH4, "Port of loading", [
    safe(() => sea.portOfLoading),
  ]);
  drawCell(margin + third * 2, yPos, third, rowH4, "Vessel / Voyage", [
    [safe(() => sea.vesselName), safe(() => sea.voyageNumber)]
      .filter(Boolean)
      .join(" / "),
  ]);
  yPos -= rowH4;

  // ── Port of discharge | Place of delivery ───────────────────────────────────
  const rowH5 = 44;
  const half = innerWidth / 2;
  const placeOfDelivery =
    safe(() => sea.placeOfDelivery) ||
    [
      safe(() => data.delivery?.address?.city),
      countryName(data.delivery?.address?.country),
    ]
      .filter(Boolean)
      .join(", ");
  drawCell(margin, yPos, half, rowH5, "Port of discharge", [
    safe(() => sea.portOfDischarge),
  ]);
  drawCell(margin + half, yPos, half, rowH5, "Place of delivery", [
    placeOfDelivery,
  ]);
  yPos -= rowH5;

  // ── Cargo details (full width) ──────────────────────────────────────────────
  const cargo = data.cargoItems ?? [];
  const totalPackages = cargo.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const totalWeight = cargo.reduce((s, i) => s + (Number(i.weightKg) || 0), 0);
  const totalVolume = cargo.reduce((s, i) => s + (Number(i.volumeM3) || 0), 0);

  const cargoLines: string[] = [];
  for (const item of cargo) {
    const text = [
      safe(() => item.marks),
      [item.quantity, item.type].filter(Boolean).join(" "),
      safe(() => item.description),
      item.weightKg ? `${item.weightKg} kg` : "",
      item.volumeM3 ? `${item.volumeM3} m³` : "",
    ]
      .filter((v) => v && v !== "")
      .join("  ·  ");
    if (text)
      wrapText(text, font, 9, innerWidth - 14, 2).forEach((l) =>
        cargoLines.push(l),
      );
  }
  if (totalPackages > 0 || totalWeight > 0) {
    cargoLines.push(
      `Total: ${totalPackages} pkg · ${totalWeight.toLocaleString("en-US")} kg${
        totalVolume > 0 ? ` · ${totalVolume} m³` : ""
      }`,
    );
  }

  const cargoH = Math.max(80, 34 + cargoLines.length * 12);
  page.drawRectangle({
    x: margin,
    y: yPos - cargoH,
    width: innerWidth,
    height: cargoH,
    borderColor: border,
    borderWidth: 1,
  });
  page.drawText("Cargo details", {
    x: margin + 6,
    y: yPos - 11,
    size: 7,
    font,
    color: labelGray,
  });
  page.drawText(
    "Marks / No. of packages / Description of goods / Gross weight / Volume",
    { x: margin + 6, y: yPos - 21, size: 7, font, color: labelGray },
  );
  let cvy = yPos - 33;
  for (const line of cargoLines) {
    if (cvy < yPos - cargoH + 5) break;
    page.drawText(line, { x: margin + 6, y: cvy, size: 9, font, color: black });
    cvy -= 12;
  }
  yPos -= cargoH;

  // ── Freight & charges | Remarks ─────────────────────────────────────────────
  const rowH6 = 46;
  const blRemarkTexts = (sea.blRemarks ?? [])
    .filter((r: any) => r?.text?.trim())
    .map((r: any) => r.text.trim());
  drawCell(margin, yPos, half, rowH6, "Freight & charges", [
    safe(() => sea.freightPaymentTerms),
  ]);
  drawCell(
    margin + half,
    yPos,
    half,
    rowH6,
    "Remarks",
    wrapText(
      blRemarkTexts.join(" · ") || safe(() => data.additionalNotes),
      font,
      9,
      half - 14,
      2,
    ),
  );
  yPos -= rowH6;

  // ── Place of issue | Date of issue | Signed by ──────────────────────────────
  const rowH7 = 46;
  drawCell(margin, yPos, third, rowH7, "Place of issue", [
    safe(() => sea.placeOfReceipt) || safe(() => data.pickup?.address?.city),
  ]);
  drawCell(margin + third, yPos, third, rowH7, "Date of issue", [
    fmtDate(sea.blDate),
  ]);
  drawCell(
    margin + third * 2,
    yPos,
    third,
    rowH7,
    "Signed by (carrier / agent)",
    [safe(() => sea.forwarder?.companyName) || safe(() => sea.shippingLine)],
  );
  yPos -= rowH7;

  page.drawText("Generated by LiveCargo", {
    x: margin,
    y: 24,
    size: 8,
    font,
    color: labelGray,
  });

  return await pdfDoc.save();
}

export async function downloadBillOfLadingPdf(
  data: Shipment,
  countries: Country[],
  filename?: string,
): Promise<void> {
  const pdfBytes = await createBillOfLadingPdf(data, countries);
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filename ||
    `House-Bill-of-Lading${
      data.referenceNumber ? `-${data.referenceNumber}` : ""
    }.pdf`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
